import "core-js/stable/structured-clone";

import {
  batch,
  Context,
  createComputed,
  createContext,
  createEffect,
  createMemo,
  FlowProps,
  getOwner,
  mapArray,
  untrack,
  useContext,
} from "solid-js";
import { createStore, reconcile, SetStoreFunction } from "solid-js/store";
import { Err } from "better-result";
import { RecordTuple } from "record-tuple";
import { ReactiveMap } from "@solid-primitives/map";

import Chainable from "@repo/utils/Chainable";
import Log from "@repo/utils/Log";
import PendingCache from "@repo/utils/PendingCache";
import SyncContext from "@repo/utils/SyncContext";
import UArray from "@repo/utils/UArray";
import UBoolean from "@repo/utils/UBoolean";
import UFunction from "@repo/utils/UFunction";
import UObject from "@repo/utils/UObject";
import UProxy from "@repo/utils/UProxy";
import UTuple from "@repo/utils/UTuple";
import UType from "@repo/utils/UType";
import StorePair from "@repo/utils-solid/StorePair";
import UOwner from "@repo/utils-solid/UOwner";
import USolid from "@repo/utils-solid/USolid";

import { lastActionsDefinitionRef } from "./runtime";

namespace FeatureStore {
  let config = {
    ErrorContext: null as null | Context<any>,
    useCaptureError:
      () =>
      (error: unknown): void => {
        throw error;
      },
    cachePendingActions: false,
    hooks: {
      init: UFunction.noop as (
        featureName: string,
        initialState: UObject.Any
      ) => void,
      setStore: UFunction.noop as (
        featureName: string,
        path: PropertyKey[],
        value: any
      ) => void,
    },
  };

  export function configure(c: UObject.DeepPartial<typeof config>) {
    config = UObject.merge(config, c);
  }

  export namespace Setup {
    type _Accessors = UFunction.Any | { [key: string]: _Accessors };

    export type Scope = {
      accessors: _Accessors;
      mutations: UType.Nested<
        Record<string, UFunction.Any.Void | UType.Nested.Children>
      >;
      actions: UType.Nested<
        Record<string, UFunction.Any | UType.Nested.Children>
      >;
      state: UObject.Any;
    };
  }

  export type Once = <R>(key: string | number, fn: () => R) => R;

  export const Context = createContext<
    StorePair<Record<string, UObject.All>> & {
      once: Once;
      pendingCache: PendingCache;
    }
  >();

  export function Provider(props: FlowProps) {
    const [store, setStore] = createStore<Record<string, UObject.Any>>({});

    createComputed(
      mapArray(
        () => Object.keys(store),
        (featureName) => {
          createComputed((prev) => {
            if (!Log.config.enabled) return;

            const current = UObject.clone(store[featureName] || {});
            Log.group`${Log.color("grey", prev ? "updated state" : "initial state")} ${Log.color("orange", featureName)}`(
              () => {
                Log.plain(current);
                if (prev)
                  Log.group`Previous state`(() => {
                    Log.plain(prev);
                  });
              }
            );

            return current;
          });
        }
      )
    );

    return (
      <Context.Provider
        value={Object.assign([store, setStore] as StorePair<any>, {
          once: UFunction.memo(
            (key: string | number, fn: UFunction.Any) => fn(),
            { by: ([key]) => key }
          ),
          pendingCache: new PendingCache(),
        })}
      >
        {props.children}
      </Context.Provider>
    );
  }

  export namespace Setup {
    type MapInternalScopes<Layers extends Layer[]> = Layers extends [
      infer L extends Layer,
      ...infer Rest extends Layer[],
    ]
      ? [_Layer.InternalScopeOf<L>, ...MapInternalScopes<Rest>]
      : [];

    type ApplyLayers<
      Layers extends Layer[],
      Key extends "accessors" | "mutations" | "actions",
    > = Layers extends []
      ? {}
      : UType.Narrow<
          UTuple.IntersectItems<
            // Scope types must be mapped before intersection, function return
            // types do not intersect properly
            MapInternalScopes<Layers>
          >,
          _Layer.Internal.SetupScope
        >[Key];

    export type Context<Layers extends Layer[] = []> = {
      attachAction: <Accessor extends UFunction.Any>(
        accessor: Accessor,
        action: (...args: Parameters<Accessor>) => any,
        checkLoaded?: (
          ...args: Parameters<Accessor>
        ) => (result: ReturnType<Accessor>) => any
      ) => Accessor & {
        loading: (...args: Parameters<Accessor>) => boolean;
        peek: Accessor;
      };
      once: Once;
      wrap: {
        accessors<T>(accessors: T): {
          finalize(): ApplyLayers<Layers, "accessors"> & T;
        };
        mutations<T>(mutations: T): {
          finalize(): ApplyLayers<Layers, "mutations"> & T;
        };
        actions<
          AbstractActions extends UType.Default<
            _Layer.AbstractActionsOf<
              UType.Narrow<UTuple.IntersectItems<Layers>, Layer>
            >,
            {}
          >,
          T extends Layers extends []
            ? Setup.Scope["actions"]
            : Partial<AbstractActions> & Setup.Scope["actions"],
        >(
          actions: T
        ): {
          declareAbstract<
            Abstract extends _Layer.AbstractActionsOf<
              UType.Narrow<UTuple.IntersectItems<Layers>, Layer>
            > extends never
              ? {}
              : _Layer.AbstractActionsOf<
                  UType.Narrow<UTuple.IntersectItems<Layers>, Layer>
                >,
          >(): {
            finalize(): ApplyLayers<Layers, "actions"> &
              T &
              Abstract & {
                [_Layer.abstract]: Abstract;
              };
          };
          finalize: Exclude<
            UObject.RequiredKeys<AbstractActions>,
            UObject.RequiredKeys<T>
          > extends never
            ? () => ApplyLayers<Layers, "actions"> & T
            : `Must implement all abstract actions or forward them with declareAbstract: ${UTuple.Join<
                UType.Narrow<
                  UTuple.FromUnion<
                    Exclude<
                      UObject.RequiredKeys<AbstractActions>,
                      UObject.RequiredKeys<T>
                    >
                  >,
                  string[]
                >,
                ", "
              >}`;
        };
      };
    };
    const accessorPathContext = new SyncContext<UObject.Key[]>([]);
    export namespace Context {
      export function build(
        name: string,
        layers: FeatureStore.Layer[] = []
      ): Setup.Context {
        const { once, pendingCache } = useContext(FeatureStore.Context)!;
        const owner = getOwner()!;

        const layerScopes = UFunction.memo(() =>
          _Layer.flatten(layers).map((layer) => layer[_Layer.internal].setup())
        );

        return {
          once: (key, fn) => once(JSON.stringify([name, key]), fn),
          wrap: {
            accessors: (accessors) => ({
              finalize: () =>
                // @ts-expect-error
                UProxy.deepDecorate.lazy(
                  // @ts-expect-error
                  UFunction.memo(() =>
                    [
                      ...layerScopes().map((scope) => scope.accessors),
                      accessors,
                    ].reduce(
                      (acc, curr) =>
                        // @ts-expect-error
                        UProxy.merge(acc, curr),
                      {}
                    )
                  ),
                  (original, path) =>
                    (...args) =>
                      accessorPathContext.run(path, () => original(...args))
                ),
            }),
            mutations: (mutations) => ({
              finalize: () =>
                // @ts-expect-error
                [
                  ...layerScopes().map((scope) => scope.mutations),
                  mutations,
                ].reduce(
                  (acc, curr) =>
                    // @ts-expect-error
                    UProxy.merge(acc, curr),
                  {}
                ),
            }),
            actions: (actions) => {
              const decoratedActions = Log.deepDecorate(
                config.cachePendingActions
                  ? pendingCache.wrap(name, actions)
                  : actions,
                `set${name}`,
                Log.join([Log.color("grey", "actions"), " "], "")
              );

              return {
                finalize: (() => (
                  (lastActionsDefinitionRef.raw = actions),
                  (lastActionsDefinitionRef.current = decoratedActions),
                  (lastActionsDefinitionRef.resolved = [
                    ...layerScopes().map((scope) => scope.actions),
                    decoratedActions,
                  ].reduce((acc, curr) => UProxy.merge(acc, curr)))
                )) as any,
                declareAbstract: (
                  useSurfaceFeature?: (() => [
                    Setup.Scope["accessors"],
                    Setup.Scope["actions"],
                  ]) &
                    Layer
                ) => ({
                  finalize() {
                    if (!useSurfaceFeature)
                      throw new Error(
                        `FeatureStore with name ${name} declares abstract actions that must be implemented. Must include this in your app with .with(...) before using components that depend on it.`
                      );

                    const owner = getOwner();

                    // Must run lazily similar to extensions since surface
                    // feature depends on layer circularly.
                    const _actions = UFunction.memo(() =>
                      UOwner.run(owner, () => {
                        const surfaceActions =
                          (useSurfaceFeature(),
                          useSurfaceFeature[_Layer.internal].setup(),
                          lastActionsDefinitionRef.current);

                        return [
                          ...layerScopes().map((scope) => scope.actions),
                          decoratedActions,
                          surfaceActions,
                        ].reduce((acc, curr) => UProxy.merge(acc, curr)) as any;
                      })
                    );

                    return UProxy.deepDecorate.lazy(
                      _actions,
                      UFunction.identity
                    );
                  },
                }),
              };
            },
          },
          attachAction: (
            accessor,
            action,
            checkLoaded = () => UBoolean.isTruthy
          ) => {
            const stateMap = new ReactiveMap<
              RecordTuple.deep.Result<any>,
              "loading" | "error" | "resolved"
            >();
            const createRef = (args: Parameters<typeof accessor>) =>
              RecordTuple.deep([name, accessorPathContext.current(), args]);

            const res = Object.assign(
              ((...args: Parameters<typeof accessor>) => {
                const ref = createRef(args);
                const res = USolid.runMemo(() => accessor(...(args as any[])));

                // Capture error in as narrow of a context as possible,
                // still want to use outer owner to run actions once.
                const innerOwner = getOwner();

                const targetOwner = !innerOwner
                  ? owner
                  : // Only use inner owner if it has an ErrorContext, accessor
                    // may be executed in a differentroot if when using
                    // UAccessor.waitUntil or otherwise.
                    UOwner.run(innerOwner, () =>
                      config.ErrorContext && useContext(config.ErrorContext)
                        ? innerOwner
                        : owner
                    )!;

                const isLoaded = createMemo(() => !!checkLoaded(...args)(res));

                if (untrack(isLoaded)) {
                  untrack(() => stateMap.set(ref, "resolved"));
                  return res;
                }

                UOwner.runOnce(owner, ref, () =>
                  UOwner.run(targetOwner, () => {
                    createEffect(() => {
                      const captureError = config.useCaptureError();

                      (async () => {
                        if (isLoaded()) return;
                        if (untrack(() => stateMap.get(ref))) return;

                        stateMap.set(ref, "loading");

                        await untrack(async () => {
                          const result = await action(...args);
                          if (result instanceof Err) throw result.error;
                          stateMap.delete(ref);
                        });
                      })()
                        .then(() => {
                          stateMap.set(ref, "resolved");
                        })
                        .catch((e) => {
                          captureError(e);
                          stateMap.set(ref, "error");
                        });
                    });
                  })
                );

                return res;
              }) as typeof accessor,
              {
                loading(...args: Parameters<typeof accessor>) {
                  return USolid.runMemo(() => {
                    res(...args);
                    // Initial value is undefined only when loading
                    return (
                      (stateMap.get(createRef(args)) || "loading") === "loading"
                    );
                  });
                },
                peek: accessor,
              }
            );
            return res;
          },
        };
      }
    }

    export const wrapSetStore =
      (name: string, setStore: SetStoreFunction<any>) =>
      (...args: any[]) => {
        if (
          config.hooks.setStore === UFunction.noop &&
          Log.config.level !== "debug"
        )
          // @ts-expect-error setStore generic is too complicated to accept
          // it's own parameters from Parameters<SetStoreFunction<any>>
          return setStore(...args);

        const lastArg = args.at(-1);
        if (typeof lastArg === "function") {
          args[args.length - 1] = (state: any) => {
            const res = lastArg(state);
            captureSetState(res);
            return res;
          };
        } else captureSetState();

        function captureSetState(lastArg = args.at(-1)) {
          config.hooks.setStore?.(name, args.slice(0, -1), lastArg);

          if (Log.config.level === "debug")
            Log.withTrace`${Log.color("grey", "set")} ${Log.fn(
              [`set${name}`],
              [...args.slice(0, -1), ...(args.length > 0 ? [lastArg] : [])]
            )}`;
        }

        // @ts-expect-error setStore generic is too complicated to accept
        // it's own parameters from Parameters<SetStoreFunction<any>>
        return setStore(...args);
      };
  }

  const useFeature = (
    name: string,
    initialState: UObject.Any,
    setup: (
      storePair: ReturnType<typeof createStore<never>>,
      context: Setup.Context
    ) => {
      accessors: Setup.Scope["accessors"];
      actions: Setup.Scope["actions"];
    },
    options: {
      layers?: FeatureStore.Layer[];
      ref: symbol;
    }
  ) => {
    const context = useContext(Context);

    if (!context)
      throw new Error(
        `use${name} must be called in child of FeatureStore.Provider.`
      );

    const [store, setStore] = (() => {
      const [store, setStore] = context;

      untrack(() => {
        if (store[name]?.[options.ref] === true) return;
        batch(() => {
          // TODO: Manually merge for performance
          setStore(
            name,
            reconcile(UObject.clone(UProxy.merge(initialState, store[name])))
          );
          setStore(name, options.ref, true);
        });
      });

      return StorePair.at(context, name) as StorePair<UObject.Any>;
    })();

    const owner = getOwner();

    const extensions = UFunction.memo(
      () =>
        UOwner.run(owner, () =>
          setup(
            [store, Setup.wrapSetStore(name, setStore)],
            Setup.Context.build(name, options.layers)
          )
        )!
    );

    // Lazily call extensions when they're referenced, this allows features to
    // circularly depend on each other since setup only execute when an accessor
    // or action is called in application code.
    return [
      UProxy.deepDecorate.lazy(
        () => extensions().accessors,
        UFunction.identity
      ),
      UProxy.deepDecorate.lazy(() => extensions().actions, UFunction.identity),
    ];
  };

  type Init<
    Name extends string,
    State extends UObject.Any,
    Layers extends Layer[] = [],
  > = (
    Layers extends []
      ? {}
      : UType.Narrow<
          UTuple.IntersectItems<Layers>,
          Layer
        >[typeof _Layer.internal]["initialState"]
  ) extends infer LayerState
    ? {
        with<M extends Layer<Name>>(
          layer: M
        ): Init<Name, State, [...Layers, M]>;
        setup<
          Accessors extends Setup.Scope["accessors"],
          Actions extends Setup.Scope["actions"],
          Mutations extends Setup.Scope["mutations"] = {},
        >(
          setup: (
            storePair: ReturnType<typeof createStore<LayerState & State>>,
            context: Setup.Context<Layers>
          ) => { accessors: Accessors; mutations?: Mutations; actions: Actions }
        ): {
          [Key in `use${Name}`]: (() => [Accessors, Actions]) &
            Layer<
              Name,
              LayerState & State,
              { accessors: Accessors; mutations: Mutations; actions: Actions }
            >;
        };
      }
    : never;

  export const init = <Name extends string, State extends UObject.Any>(
    name: Name,
    initialState: State
  ) =>
    Chainable.create(
      Chainable.wrapFactory(({ layers = [] as FeatureStore.Layer[] }) => ({
        setup: <
          Accessors extends Setup.Scope["accessors"],
          Actions extends Setup.Scope["actions"],
          Mutations extends Setup.Scope["mutations"] = {},
        >(
          setup: (
            storePair: ReturnType<typeof createStore<State>>,
            context: Setup.Context
          ) => {
            accessors: Accessors;
            mutations?: Mutations;
            actions: Actions;
          }
        ) => {
          const featureRef = Symbol(`feature:${name}`);
          const use = Object.assign(
            () =>
              useFeature(
                name,
                [
                  ..._Layer
                    .flatten(layers)
                    .map((layer) => layer[_Layer.internal].initialState),
                  initialState,
                ].reduce((acc, curr) => UProxy.merge(acc, curr), {}),
                setup,
                { layers, ref: featureRef }
              ),
            {
              [_Layer.internal]: {
                name,
                initialState,
                layers,
                setup() {
                  const context = useContext(Context);
                  if (!context)
                    throw new Error(
                      `use${name} must be called in child of FeatureStore.Provider.`
                    );

                  const storePair = StorePair.at(
                    context,
                    name
                  ) as StorePair<State>;

                  return setup(storePair, Setup.Context.build(name, layers));
                },
              } satisfies _Layer.Internal,
            }
          );

          return {
            [`use${name}`]: use,
          } as {
            [Key in `use${Name}`]: typeof use;
          };
        },
      })),
      {
        with: (options) => (layer: FeatureStore.Layer) => ({
          ...options,
          layers: [...(options.layers || []), layer],
        }),
      }
    ) as unknown as Init<Name, State>;

  // Inferring type from createLayer causes circular dependency, with Setup.Context. Must define explicitly
  export type Layer<
    Name extends string = string,
    State extends UObject.Any = UObject.Any,
    Scope extends Pick<Setup.Scope, "accessors" | "actions" | "mutations"> =
      Pick<Setup.Scope, "accessors" | "actions" | "mutations">,
  > = {
    [_Layer.internal]: {
      name: Name;
      initialState: State;
      layers: Layer[];
      setup: () => Scope;
    };
  };
  export namespace _Layer {
    export type Internal = {
      name: string;
      initialState: UObject.Any;
      layers: Layer[];
      setup: () => Internal.SetupScope;
    };
    export namespace Internal {
      export type SetupScope = UObject.PartialBy<
        Pick<Setup.Scope, "accessors" | "actions" | "mutations">,
        "mutations"
      >;
    }
    export type InternalScopeOf<M extends Layer> = M extends {
      [_Layer.internal]: { setup: () => infer I };
    }
      ? I
      : never;

    export type WithAbstractActions<Abstract extends UObject.Any> = {
      [_Layer.internal]: {
        setup: () => { actions: { [_Layer.abstract]: Abstract } };
      };
    };
    export declare const abstract: unique symbol;
    export const internal = Symbol("internal");
    export type AbstractActionsOf<M extends Layer> =
      M extends WithAbstractActions<infer Abstract> ? Abstract : {};

    export const flatten = (layers: Layer[]): Layer[] =>
      layers.length === 0
        ? []
        : UArray.dedupe([
            ...layers.flatMap((layer) =>
              flatten(layer[_Layer.internal].layers)
            ),
            ...layers,
          ]);
  }
  export namespace Actions {
    export type From<UseContextFn extends () => [any, any]> =
      ReturnType<UseContextFn>[1];
  }
}

export default FeatureStore;
